import { useEffect } from "react";
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";

import { authBase } from "./api/client";
import { useMe } from "./api/me";
import { setSentryUser } from "./lib/sentry";
import Compare from "./routes/Compare";
import Dashboard from "./routes/Dashboard";
import Discover from "./routes/Discover";
import Hindsight from "./routes/Hindsight";
import Legal from "./routes/Legal";
import Portfolio from "./routes/Portfolio";
import Sectors from "./routes/Sectors";
import Settings from "./routes/Settings";
import SmartMoney from "./routes/SmartMoney";
import SymbolDetail from "./routes/SymbolDetail";
import Telemetry from "./routes/Telemetry";
import Trending from "./routes/Trending";

function Login() {
  const denied =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("denied")
      : null;

  let banner: React.ReactNode = null;
  if (denied === "invite") {
    banner = (
      <div className="mb-5 rounded-lg border border-amber-900/60 bg-amber-950/40 p-3 text-left text-xs text-amber-200">
        stux is in closed alpha. To request access, email{" "}
        <a
          href="mailto:mehdi.towhidi@gmail.com?subject=stux%20alpha%20access"
          className="underline hover:text-amber-100"
        >
          mehdi.towhidi@gmail.com
        </a>
        .
      </div>
    );
  } else if (denied === "disabled") {
    banner = (
      <div className="mb-5 rounded-lg border border-rose-900/60 bg-rose-950/40 p-3 text-left text-xs text-rose-200">
        This account has been disabled.
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-sm rounded-2xl border border-slate-800 bg-slate-900 p-6 text-center shadow-xl sm:p-8">
        <div className="mb-2 text-2xl font-semibold tracking-tight">stux</div>
        <p className="mb-6 text-sm text-slate-400">
          Personal stock monitor with AI analysis-driven recommendations.{" "}
          <span className="text-amber-400">Not financial advice.</span>
        </p>
        {banner}
        <a
          href={`${authBase}/google/login`}
          className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-500 px-4 py-3 text-sm font-medium text-white shadow-sm transition hover:bg-sky-400 active:bg-sky-600"
        >
          <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
            <path
              fill="#FFC107"
              d="M21.8 10.2H12v3.85h5.6c-.24 1.27-.96 2.36-2.05 3.07v2.55h3.32c1.94-1.79 3.06-4.43 3.06-7.55 0-.66-.05-1.3-.13-1.92Z"
            />
            <path
              fill="#FF3D00"
              d="m3.93 7.31 2.85 2.09C7.6 7.6 9.66 6.27 12 6.27c1.45 0 2.77.5 3.81 1.32L18.7 4.7C16.85 3.05 14.55 2 12 2 8.06 2 4.7 4.16 3.05 7.31h.88Z"
            />
            <path
              fill="#4CAF50"
              d="M12 22c2.55 0 4.7-.85 6.27-2.31l-2.95-2.27c-.84.56-1.96.92-3.32.92-2.6 0-4.81-1.66-5.6-3.97H3.27v2.5C4.92 19.86 8.27 22 12 22Z"
            />
            <path
              fill="#1976D2"
              d="M21.8 10.2H12v3.85h5.6c-.24 1.27-.96 2.36-2.05 3.07v2.55h3.32c1.94-1.79 3.06-4.43 3.06-7.55 0-.66-.05-1.3-.13-1.92Z"
            />
          </svg>
          Sign in with Google
        </a>
        <p className="mt-5 text-[11px] text-slate-500">
          By signing in you accept the{" "}
          <Link to="/legal" className="underline hover:text-slate-300">
            disclaimer
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { data, isLoading } = useMe();
  const location = useLocation();
  useEffect(() => {
    if (data?.authenticated) {
      setSentryUser({ id: data.id, email: data.email });
    } else if (data && !data.authenticated) {
      setSentryUser(null);
    }
  }, [data?.authenticated, data?.id, data?.email]);
  if (isLoading) return <div className="p-8 text-slate-400">Loading…</div>;
  if (!data?.authenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/legal" element={<Legal />} />
      <Route
        path="/"
        element={
          <RequireAuth>
            <Dashboard />
          </RequireAuth>
        }
      />
      <Route
        path="/discover"
        element={
          <RequireAuth>
            <Discover />
          </RequireAuth>
        }
      />
      <Route
        path="/trending"
        element={
          <RequireAuth>
            <Trending />
          </RequireAuth>
        }
      />
      <Route
        path="/portfolio"
        element={
          <RequireAuth>
            <Portfolio />
          </RequireAuth>
        }
      />
      <Route
        path="/hindsight"
        element={
          <RequireAuth>
            <Hindsight />
          </RequireAuth>
        }
      />
      <Route
        path="/smartmoney"
        element={
          <RequireAuth>
            <SmartMoney />
          </RequireAuth>
        }
      />
      <Route
        path="/sectors"
        element={
          <RequireAuth>
            <Sectors />
          </RequireAuth>
        }
      />
      <Route
        path="/compare"
        element={
          <RequireAuth>
            <Compare />
          </RequireAuth>
        }
      />
      <Route
        path="/s/:yahoo"
        element={
          <RequireAuth>
            <SymbolDetail />
          </RequireAuth>
        }
      />
      <Route
        path="/telemetry"
        element={
          <RequireAuth>
            <Telemetry />
          </RequireAuth>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireAuth>
            <Settings />
          </RequireAuth>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
